/*!
 * Roundway Bandit Loader
 * Single source of truth for the per-pageload behavr-decide call.
 * Every popup script awaits window.RoundwayBandit.ready and honours .format.
 *
 *  window.RoundwayBandit = {
 *    ready: Promise<{ chosen, impressionId, sessionId, format, suppress }>,
 *    format: 'standard' | 'quiz' | 'scratch' | null,   // set once ready resolves
 *    chosen: {...},                                    // ditto
 *    impressionId, sessionId
 *  }
 *
 * Config via window.RoundwayBanditConfig = { apiBase, anonKey, popupId? }
 * or via <script src=... data-api-base="..." data-anon-key="...">.
 */
(function () {
  'use strict';
  if (window.RoundwayBandit && window.RoundwayBandit.ready) return;

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'rwb-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  // Resolve config: window object > current <script data-*> attrs
  var cfg = window.RoundwayBanditConfig || {};
  if (!cfg.apiBase || !cfg.anonKey) {
    var scripts = document.getElementsByTagName('script');
    for (var i = scripts.length - 1; i >= 0; i--) {
      var s = scripts[i];
      if (s.src && /roundway-bandit\.js/.test(s.src)) {
        cfg.apiBase = cfg.apiBase || s.getAttribute('data-api-base') || '';
        cfg.anonKey = cfg.anonKey || s.getAttribute('data-anon-key') || '';
        cfg.popupId = cfg.popupId || s.getAttribute('data-popup-id') || 'default';
        break;
      }
    }
  }
  cfg.popupId = cfg.popupId || 'default';

  // ── Popup blocklist (2026-09-07) ──
  // Lead-capture pages own the visitor: never overlay a sitewide popup on the
  // Aquafibre/GLP-1 entry form or the health quiz. Blocking here also blocks
  // the teaser pill and the game arm.
  var BLOCKED_PAGES = /^\/pages\/(glp1|glp1-terms|quiz|health-quiz)(\/|$)/i;
  var PAGE_BLOCKED = false;
  try { PAGE_BLOCKED = BLOCKED_PAGES.test(location.pathname); } catch (_) {}
  if (PAGE_BLOCKED) {
    window.__RW_BLOCK_POPUPS__ = true;
    window.__RW_SUPPRESS_LEGACY_POPUP__ = true;
    try { sessionStorage.setItem('rw_popup_dismissed_session', '1'); } catch (_) {}
    window.RoundwayBandit = {
      ready: Promise.resolve({ chosen: {}, impressionId: null, sessionId: null, format: null, suppress: true }),
      format: null, chosen: {}, impressionId: null, sessionId: null, suppress: true, blocked: true
    };
    return;
  }


  // ── Game popup RETIRED (2026-09-07) ──
  // 10-day read: standard popup 3,214 views / 225 submits (7.0%); game arm
  // 362 views / 8 submits (2.2%). Game only opens via ?rw_force_format=game.
  //
  // ── Micro-quiz arm RETIRED (2026-09-15) ──
  // 21-day read: standard popup 7,962 views / 500 submits (6.3%); micro-quiz
  // 210 views / 8 submits (3.8%), with no separation between its three copy
  // variants. That separate design stays retired; any new quiz test now runs
  // only inside the original split-image popup's left panel.
  // Micro-quiz and game only open via ?rw_force_format=...
  //
  // ── One visual system (2026-09-17) ──
  // The separate micro-quiz and capture modal designs cut conversion from the
  // original shell's 5.4–5.9% to 2.2–3.1%. The original shell's own Behavr
  // decision now tests standard vs quiz content in its left panel. This loader
  // must stay on `standard` so it never suppresses that shared shell.
  //
  // ── Quiz content retired too (2026-09-18) ──
  // The in-panel quiz ran at 1.1% against the shell's 6-12%. Its variants are
  // now inactive in behavr_variants, and every alternative format here is gone:
  // there is exactly one popup, the original split-image shell, and no force
  // flag can summon a retired design.
  var SPLIT_ARM = 'standard';



  // Session id shared across popups for this tab
  var sessionId;
  try {
    sessionId = sessionStorage.getItem('rw_session_id');
    if (!sessionId) { sessionId = uuid(); sessionStorage.setItem('rw_session_id', sessionId); }
  } catch (_) { sessionId = uuid(); }

  var state = {
    ready: null,
    splitArm: SPLIT_ARM,
    format: null,
    chosen: null,
    impressionId: null,
    sessionId: sessionId,
    suppress: false,
    config: cfg
  };

  function applyForce(result) {
    // The original shell owns its standard-vs-quiz content decision. Retired
    // specialist formats remain available only through explicit QA links.
    result.format = SPLIT_ARM;
    return result;
  }

  function setBodyClass(format) {
    try {
      var b = document.body;
      if (!b) return;
      b.classList.remove('rw-format-standard', 'rw-format-quiz', 'rw-format-scratch', 'rw-format-microquiz', 'rw-format-game', 'rw-format-capture');
      if (format) b.classList.add('rw-format-' + format);
    } catch (_) {}
  }


  function suppressLegacyIfNotStandard(format) {
    // When the bandit picks the game arm, keep the legacy standard popup
    // hidden for this session. The standard popup reads
    // window.__RW_SUPPRESS_LEGACY_POPUP__ in isUniversalQuizTakeover(); the
    // quiz-killer snippet wipes the rw_popup_quiz_active storage keys but
    // never this window flag, so it survives.
    if (format === 'standard' || format === 'quiz' || format === 'scratch') return;
    try {
      window.__RW_SUPPRESS_LEGACY_POPUP__ = true;
      sessionStorage.setItem('rw_popup_dismissed_session', '1');
    } catch (_) {}
  }

  state.ready = new Promise(function (resolve) {
    // The theme's original popup makes the production Behavr decision itself.
    // Resolve without a second request here, which would double-count a
    // standard impression and distort the standard-vs-quiz result.
    if (SPLIT_ARM === 'standard') {
      var sharedShell = { chosen: {}, impressionId: null, sessionId: sessionId, format: 'standard', suppress: false };
      state.format = sharedShell.format;
      state.chosen = sharedShell.chosen;
      setBodyClass(state.format);
      resolve(sharedShell);
      return;
    }

    if (!cfg.apiBase) {
      var fallback = applyForce({ chosen: {}, impressionId: null, sessionId: sessionId, format: 'standard', suppress: false });
      state.format = fallback.format; state.chosen = fallback.chosen;
      setBodyClass(state.format);
      suppressLegacyIfNotStandard(state.format);
      resolve(fallback);
      return;
    }

    var controller = window.AbortController ? new AbortController() : null;
    var timeout = setTimeout(function () { if (controller) controller.abort(); }, 1800);
    fetch(cfg.apiBase + '/behavr-decide', {
      method: 'POST',
      signal: controller ? controller.signal : undefined,
      headers: {
        'Content-Type': 'application/json',
        apikey: cfg.anonKey,
        Authorization: 'Bearer ' + cfg.anonKey
      },
      body: JSON.stringify({
        popup_id: cfg.popupId,
        session_id: sessionId,
        force_format: SPLIT_ARM,
        page: location.pathname,

        referrer: document.referrer || null,
        utm_source: (location.search.match(/[?&]utm_source=([^&]+)/) || [])[1] || null,
        returning: !!(document.cookie.indexOf('rw_returning=1') >= 0)
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        clearTimeout(timeout);
        var chosen = (j && j.chosen) || {};
        var format = 'standard';
        var result = applyForce({
          chosen: chosen,
          impressionId: (j && j.impression_id) || null,
          sessionId: (j && j.session_id) || sessionId,
          format: format,
          suppress: !!(j && j.suppress)
        });
        state.format = result.format;
        state.chosen = result.chosen;
        state.impressionId = result.impressionId;
        state.sessionId = result.sessionId;
        state.suppress = result.suppress;
        setBodyClass(state.format);
        suppressLegacyIfNotStandard(state.format);
        try { document.dispatchEvent(new CustomEvent('rw:bandit:ready', { detail: result })); } catch (_) {}
        resolve(result);
      })
      .catch(function (e) {
        clearTimeout(timeout);
        var fallback = applyForce({ chosen: {}, impressionId: null, sessionId: sessionId, format: 'standard', suppress: false });
        state.format = fallback.format;
        setBodyClass(state.format);
        suppressLegacyIfNotStandard(state.format);
        resolve(fallback);
      });
  });

  window.RoundwayBandit = state;
})();
